import {
  Combobox as ChakraCombobox,
  createListCollection,
  Portal,
  useFilter,
} from '@chakra-ui/react';
import { compact, uniq } from 'lodash';
import { forwardRef, useMemo } from 'react';

import Icon from 'ui/Icon';

const ComboboxRoot = forwardRef<HTMLDivElement, ChakraCombobox.RootProps>(
  ({ children, ...props }, ref) => (
    <ChakraCombobox.Root
      ref={ref}
      required
      openOnClick
      inputBehavior="autohighlight"
      {...props}
    >
      {children}
    </ChakraCombobox.Root>
  )
);

const ComboboxInput = forwardRef<HTMLInputElement, ChakraCombobox.InputProps>(
  ({ children, ...props }, ref) => (
    <ChakraCombobox.Control>
      <ChakraCombobox.Input
        ref={ref}
        background="white"
        border="1px solid"
        borderColor="gray.400"
        focusRing="mixed"
        focusRingColor="blue.500"
        focusRingWidth="1px"
        paddingLeft={2}
        _hover={{ borderColor: 'gray.500' }}
        _invalid={{
          backgroundColor: 'red.50',
          borderColor: 'red.400',
          focusRingColor: 'red.400',
          _hover: { borderColor: 'red.500' },
        }}
        _disabled={{
          borderColor: 'gray.400 !important',
          background: 'gray.50',
          color: 'gray.400',
          opacity: 1,
        }}
        {...props}
      />
      <ChakraCombobox.IndicatorGroup>
        <ChakraCombobox.Trigger />
      </ChakraCombobox.IndicatorGroup>
    </ChakraCombobox.Control>
  )
);

const ComboboxContent = forwardRef<HTMLDivElement, ChakraCombobox.ContentProps>(
  ({ children, ...props }, ref) => (
    <Portal>
      <ChakraCombobox.Positioner>
        <ChakraCombobox.Content ref={ref} {...props}>
          {children}
        </ChakraCombobox.Content>
      </ChakraCombobox.Positioner>
    </Portal>
  )
);

const ComboboxItem = forwardRef<
  HTMLDivElement,
  ChakraCombobox.ItemProps & { isCreatable?: boolean }
>(({ children, item, isCreatable, ...props }, ref) => (
  <ChakraCombobox.Item
    ref={ref}
    cursor="pointer"
    justifyContent="flex-start"
    item={item}
    {...props}
  >
    {isCreatable && <Icon icon="plus" boxSize={4} />}
    {item.label}
    <ChakraCombobox.ItemIndicator ml="auto" />
  </ChakraCombobox.Item>
));

const Combobox = {
  Root: ComboboxRoot,
  Input: ComboboxInput,
  Content: ComboboxContent,
  Item: ComboboxItem,
};

/*
 * Prepares a collection of items for the Combobox component.
 * It filters the items based on the input value and selected value.
 */
export const useFilteredCollection = (
  allValues: string[],
  inputValue: string,
  selectedValue: string
) => {
  const { contains } = useFilter({ sensitivity: 'base' });
  const filteredCollection = useMemo(
    () =>
      createListCollection({
        items: compact(uniq([...allValues, inputValue]))
          .filter(
            // Apply filtering only if the search text doesn't match the selection
            (s) => selectedValue === inputValue || contains(s, inputValue)
          )
          .map((item) => ({
            value: item,
            label: item,
          })),
      }),
    [allValues, inputValue, selectedValue, contains]
  );
  return filteredCollection;
};

export default Combobox;
